import * as Sentry from '@sentry/react';

let initialized = false;

/**
 * Idempotently initialize Sentry if REACT_APP_SENTRY_DSN is set.
 * Without a DSN this is a no-op — Sentry methods become silent stubs.
 * Call once from the entry point (src/index.js).
 */
export function initErrorReporter() {
  if (initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Intentional: no DSN configured (dev, or prod before Sentry is wired up).
    // Leave initialized=false so a later DSN injection could still init.
    return;
  }
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    // Conservative sampling — bump up once we know the volume profile.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
  initialized = true;
}

/**
 * Report an unexpected error. Safe to call before initErrorReporter — Sentry's
 * own internals no-op when not initialized.
 *
 * @param {unknown} error  the thrown value
 * @param {object} [extra] additional context to attach (free-form object)
 */
export function reportError(error, extra) {
  // Always keep the console line — developers reading prod console / Vercel
  // logs / etc. shouldn't lose visibility because Sentry is the new sink.
  console.error(error, extra);
  if (extra) {
    Sentry.withScope((scope) => {
      scope.setExtras(extra);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Tag the current Sentry scope with the signed-in user. Call from AuthContext
 * when auth state changes. Pass null on sign-out to clear.
 */
export function setReporterUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email });
}
